<script setup lang="ts">
import type { WebSearchResult } from '@@/bits/web_search'
import { nextTick, ref } from 'vue'

defineProps<{
  results: WebSearchResult[]
}>()

const emit = defineEmits<{
  (name: 'select', url: string): void
}>()

const hoveringOn = ref<number | null>(null)

function onSelect(url: string) {
  emit('select', url)
}

function onHover(index: number) {
  nextTick(() => (hoveringOn.value = index))
}

function onStopHover() {
  hoveringOn.value = null
}
</script>

<template>
  <ul :class="['grid', 'grid-cols-1', 'mx-0', '-mt-1', 'md:mt-0', 'md:pt-2']">
    <li
      v-for="(result, index) in results"
      :key="'web-result-' + index"
      :class="['flex', 'flex-col', 'pt-0', 'pb-0', 'last:mb-2', 'px-0', 'relative']"
      @mouseenter.stop="onHover(index)"
      @mouseleave.stop="onStopHover"
      @focusin="onHover(index)"
      @focusout="onStopHover"
      @click="onSelect(result.url)"
      @keydown.enter="onSelect(result.url)"
      @keydown.space="onSelect(result.url)"
    >
      <div
        tabindex="0"
        :class="[
          'rounded-xl',
          'focus:ring-2',
          'focus:ring-grape-500',
          'dark:focus:ring-canary-500',
          'focus:ring-opacity-100',
          'hover-hover:hover:ring-2',
          'hover-hover:hover:ring-grape-500',
          'hover-hover:dark:hover:ring-canary-500',
          'hover-hover:hover:ring-opacity-100',
          'only:hide-separator',
          'last:hide-separator',
          'cursor-pointer',
          'px-2',
          'py-3',
        ]"
      >
        <div class="font-semibold text-17-22 text-highway-100 dark:text-white-100 mb-1">{{ result.title }}</div>
        <div class="font-normal text-body-extra-small leading-4 text-highway-100 dark:text-white-100 mb-1">
          {{ result.description }}
        </div>
        <div class="truncate font-normal text-12-15 mt-1 text-highway-200 dark:text-white-200">{{ result.url }}</div>
      </div>
      <div
        :class="[
          'separator',
          'border-solid',
          'border-highway-disabled',
          'dark:border-white-disabled',
          'border-t',
          'absolute',
          'bottom-0',
          'left-0',
          'right-0',
          hoveringOn === index || (hoveringOn != null && hoveringOn - 1 === index) ? 'hidden' : '',
        ]"
      />
    </li>
  </ul>
</template>
